import React from 'react';
import { GoogleMap, LoadScript, Polyline, Marker } from '@react-google-maps/api';
import decodePolyline from 'decode-google-map-polyline';
import { Ride, OrderStatus, RideType, StopActionType } from '../tracking/entities';
import gettEnv from '../../config/gettEnv';
import Bounds from './Bounds';

import pickupIcon from './images/marker-pickup-map.svg';
import dropoffIcon from './images/marker-dropoff-map.svg';

import styles from './styles';
import DriverMarker from './DriverMarker';
import { LatLngStatic } from '../common/entities';
import { ConvertCoord, FindStoppoint } from '../common/utils';

type Props = {
    ride: Ride;
    token: string;
};

// load script:
const loadScriptOptions = {
    id: 'script-loader',
    apiKey: gettEnv.GOOGLE_API_KEY,
};

const greedy = 'greedy' as const;
// map:
const mapOptions = {
    id: 'TrackerMap',
    zoom: 11,

    center: { lat: 0, lng: 0 }, //default location

    mapContainerStyle: {
        height: '440px',
    },

    options: {
        disableDefaultUI: true,
        heading: 120,
        styles: styles,
        gestureHandling: greedy,
    },
};

// polyline:
const polylineOptions = {
    geodesic: true,
    strokeColor: '#339CFF', // blue
    strokeOpacity: 1,
    strokeWeight: 5,
    zIndex: 1,
};

const shouldSkipOrigin = (type: RideType | null | undefined) => {
    return RideType.RIDE_TYPE_RECIPIENT === type || RideType.RIDE_TYPE_SENDER === type;
};

export const Map = (props: Props) => {
    const getBounds = (
        status: OrderStatus | null | undefined,
        origin: LatLngStatic | null | undefined,
        destination: LatLngStatic | null | undefined,
        driverLocation: LatLngStatic | null | undefined
    ) => {
        const bounds: LatLngStatic[] = [];
        switch (status) {
            case OrderStatus.ORDER_STATUS_CONFIRMED:
                origin ? bounds.push(origin) : destination && bounds.push(destination);
                driverLocation && bounds.push(driverLocation);
                break;
            case OrderStatus.ORDER_STATUS_DRIVING:
                destination && bounds.push(destination);
                driverLocation && bounds.push(driverLocation);
                break;
            default:
                origin && bounds.push(origin);
                destination && bounds.push(destination);
                break;
        }
        decodedPolyline && bounds.push(...decodedPolyline);
        if (bounds.length === 1) {
            // To prevent overzoom in case of only one point we have to add 2 more points with same offset to keep map centered on that point
            const center = bounds[0];
            const extendPoint1 = new LatLngStatic(center.lat + 0.01, center.lng + 0.01);
            const extendPoint2 = new LatLngStatic(center.lat - 0.01, center.lng - 0.01);
            bounds.push(extendPoint1, extendPoint2);
        }
        return bounds;
    };

    const { routeDetails, orderStatus, division, stops, type } = props.ride;

    const originStopPoint = !shouldSkipOrigin(type) ? FindStoppoint(stops, StopActionType.STOP_ACTION_PICKUP) : null;
    //In case of delivery we are not showing origin in the map

    const destinationStopPoint = FindStoppoint(stops, StopActionType.STOP_ACTION_DROP_OFF);

    const origin = originStopPoint?.location ? ConvertCoord(originStopPoint.location?.coordinates) : null;
    const destination = destinationStopPoint?.location
        ? ConvertCoord(destinationStopPoint.location?.coordinates)
        : null;

    const decodedPolyline = routeDetails?.polyline && decodePolyline(routeDetails?.polyline);

    const markerDriverPosition = routeDetails?.driverLocation && ConvertCoord(routeDetails?.driverLocation);
    const driverBearing = routeDetails?.bearing || 0;
    const bounds = getBounds(orderStatus, origin, destination, markerDriverPosition);

    return (
        <div>
            <LoadScript id={loadScriptOptions.id} googleMapsApiKey={loadScriptOptions.apiKey}>
                <GoogleMap
                    id={mapOptions.id}
                    zoom={mapOptions.zoom}
                    mapContainerStyle={mapOptions.mapContainerStyle}
                    options={mapOptions.options}
                >
                    {markerDriverPosition && (
                        <DriverMarker
                            image={division?.picture}
                            position={markerDriverPosition}
                            bearing={driverBearing}
                        />
                    )}
                    {origin && <Marker title='pickup' icon={pickupIcon} position={origin} />}
                    {destination && <Marker title='dropoff' icon={dropoffIcon} position={destination} />}
                    {decodedPolyline && <Polyline path={decodedPolyline} options={polylineOptions} />}
                    {bounds && <Bounds coordinates={bounds} padding={{ bottom: 10, top: 10, left: 10, right: 10 }} />}
                </GoogleMap>
            </LoadScript>
        </div>
    );
};

export default Map;
